import { Component, OnInit, OnDestroy, HostListener, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { Subject, takeUntil } from 'rxjs';
import { LevelPaneComponent } from './level-pane/level-pane.component';
import { ImageProcessingToolService, SessionState, LevelState } from '../../services/image-processing-tool.service';

interface PaneState {
  focused: boolean;
  type: 'nav' | 'work';
  level: number;
}

@Component({
  selector: 'app-image-processing-tool',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatMenuModule,
    LevelPaneComponent
  ],
  templateUrl: './image-processing-tool.component.html',
  styleUrls: ['./image-processing-tool.component.scss']
})
export class ImageProcessingToolComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroy$ = new Subject<void>();
  
  // ViewChild for levels container scroll
  @ViewChild('levelsContainer') levelsContainer!: ElementRef<HTMLElement>;
  
  // Session state
  sessionState: SessionState | null = null;
  isLoading = false;
  
  // Pane focus tracking
  focusedPane: PaneState = { focused: false, type: 'nav', level: 1 };
  
  // Scroll animation
  private scrollAnimationId: number | null = null;
  
  // Dynamic levels array - will be populated based on session state
  get levels(): number[] {
    const levelCount = this.sessionState?.levels?.length || 5;
    return Array.from({ length: levelCount }, (_, i) => i + 1);
  }
  
  // Level pane widths (resizable) - dynamic based on level count
  levelPaneWidths: number[] = [];
  
  getLevelWidth(levelIndex: number): number {
    if (this.levelPaneWidths[levelIndex] !== undefined) {
      return this.levelPaneWidths[levelIndex];
    }
    // Default equal width distribution
    const levelCount = this.levels.length;
    return 100 / levelCount;
  }
  
  // Initialize level pane widths when level count changes
  private initializeLevelPaneWidths(): void {
    const levelCount = this.levels.length;
    const defaultWidth = 100 / levelCount;
    // Only initialize if not already set or count changed
    if (this.levelPaneWidths.length !== levelCount) {
      this.levelPaneWidths = Array(levelCount).fill(defaultWidth);
    }
  }
  
  // Navigation pane widths within each level (%)
  navPaneWidths: number[] = [];
  
  // Initialize nav pane widths for a level
  getNavPaneWidth(level: number): number {
    if (!this.navPaneWidths[level - 1]) {
      this.navPaneWidths[level - 1] = 30; // Default 30%
    }
    return this.navPaneWidths[level - 1];
  }
  
  // ==================== CONFIGURABLE LEVEL WIDTHS ====================
  // Matching Python's PANE_WIDTH_PERCENTAGE configuration
  static readonly LEVEL_WIDTH_PERCENTAGE = 0.85; // 85% of reference (850px)
  static readonly REFERENCE_WIDTH = 1000; // Reference width for calculations
  static readonly DEFAULT_LEVEL_WIDTH = ImageProcessingToolComponent.REFERENCE_WIDTH * ImageProcessingToolComponent.LEVEL_WIDTH_PERCENTAGE;
  static readonly MIN_LEVEL_WIDTH = 500; // Minimum width when resizing
  static readonly MAX_LEVEL_WIDTH = 1200; // Maximum width when resizing
  
  // Level widths array (in pixels) - dynamically sized
  levelWidths: number[] = [];
  
  // Initialize level widths when levels change
  private initializeLevelWidths(): void {
    const levelCount = this.levels.length;
    if (this.levelWidths.length !== levelCount) {
      this.levelWidths = Array(levelCount).fill(ImageProcessingToolComponent.DEFAULT_LEVEL_WIDTH);
      this.addDebugLog(`[INIT] Level widths initialized: ${levelCount} levels at ${ImageProcessingToolComponent.DEFAULT_LEVEL_WIDTH}px each`);
    }
  }
  
  // Resizing state
  isResizingLevel = false;
  isResizingNav = false;
  resizingLevelIndex = -1;
  resizingNavLevel = -1;
  startX = 0;
  startWidth = 0;
  
  // Pin Level 1 checkbox
  pinLevel1 = false;
  
  // Debug logs
  debugLogs: string[] = [];
  showDebugPanel = false;
  showLogSection = true; // Log section below levels (like Python UI)
  
  // Keyboard navigation state - dynamically updated based on level count
  get paneOrder(): PaneState[] {
    return this.levels.map(level => ({ focused: false, type: 'nav' as const, level }));
  }
  currentPaneIndex = 0;
  
  constructor(
    private imageToolService: ImageProcessingToolService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}
  
  ngOnInit(): void {
    this.addDebugLog('[INIT] Image Processing Tool starting...');
    this.addDebugLog('[INIT] Initializing local session (no API required for browsing)');
    this.initializeLocalSession();
    
    // Subscribe to session state changes
    this.imageToolService.sessionState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.sessionState = state;
        // Initialize level pane widths when levels change
        this.initializeLevelPaneWidths();
        // Initialize level widths (pixel-based for resizing)
        this.initializeLevelWidths();
        
        // Log level changes
        if (state?.levels) {
          const levelSummary = state.levels.map((lvl, idx) => 
            `L${idx + 1}:${lvl.images?.length || 0}`
          ).join(' | ');
          console.log(`[STATE] Levels updated: ${levelSummary}`);
        }
        
        this.cdr.detectChanges();
      });
    
    this.addDebugLog('[INIT] Ready - Click "Browse Files" to load images locally');
  }
  
  ngAfterViewInit(): void {
    // Focus first level initially with scroll
    setTimeout(() => {
      this.focusPaneWithScroll(1);
    }, 300);
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  /**
   * Initialize session locally without API call
   */
  initializeLocalSession(): void {
    this.imageToolService.initializeLocalSession();
    const sessionId = this.sessionState?.sessionId || 'pending';
    this.addDebugLog(`[SESSION] Local session created: ${sessionId.substring(0, 12)}...`);
    this.addDebugLog(`[SESSION] 5 levels initialized (images load locally, no API needed)`);
  }
  
  // ==================== Keyboard Navigation ====================
  
  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    // Tab / Shift+Tab for pane navigation
    if (event.key === 'Tab') {
      event.preventDefault();
      if (event.shiftKey) {
        this.focusPreviousPane();
      } else {
        this.focusNextPane();
      }
      return;
    }
    
    // Handle other shortcuts only when a pane is focused
    if (!this.focusedPane.focused) return;
    
    // Ctrl+Z for undo
    if (event.ctrlKey && event.key === 'z' && !event.shiftKey) {
      event.preventDefault();
      this.undo();
      return;
    }
    
    // Ctrl+Shift+Z or Ctrl+Y for redo
    if ((event.ctrlKey && event.shiftKey && event.key === 'z') || 
        (event.ctrlKey && event.key === 'y')) {
      event.preventDefault();
      this.redo();
      return;
    }
    
    // Ctrl+S for save
    if (event.ctrlKey && event.key === 's') {
      event.preventDefault();
      this.saveLevel(this.focusedPane.level);
      return;
    }
    
    // Delete key for deleting selected images
    if (event.key === 'Delete') {
      event.preventDefault();
      this.deleteSelectedImages(this.focusedPane.level);
      return;
    }
  }
  
  focusNextPane(): void {
    this.paneOrder[this.currentPaneIndex].focused = false;
    this.currentPaneIndex = (this.currentPaneIndex + 1) % this.paneOrder.length;
    this.paneOrder[this.currentPaneIndex].focused = true;
    this.focusedPane = { ...this.paneOrder[this.currentPaneIndex] };
    this.scrollToCenterLevel(this.focusedPane.level);
    this.addDebugLog(`Focused: Level ${this.focusedPane.level} ${this.focusedPane.type === 'nav' ? 'Navigation' : 'Work Area'}`);
  }
  
  focusPreviousPane(): void {
    this.paneOrder[this.currentPaneIndex].focused = false;
    this.currentPaneIndex = (this.currentPaneIndex - 1 + this.paneOrder.length) % this.paneOrder.length;
    this.paneOrder[this.currentPaneIndex].focused = true;
    this.focusedPane = { ...this.paneOrder[this.currentPaneIndex] };
    this.scrollToCenterLevel(this.focusedPane.level);
    this.addDebugLog(`Focused: Level ${this.focusedPane.level} ${this.focusedPane.type === 'nav' ? 'Navigation' : 'Work Area'}`);
  }
  
  focusPaneWithScroll(level: number, type: 'nav' | 'work' = 'nav'): void {
    // Update pane order focus state
    this.paneOrder.forEach((pane, index) => {
      if (pane.level === level) {
        pane.focused = true;
        this.currentPaneIndex = index;
      } else {
        pane.focused = false;
      }
    });
    this.focusedPane = { focused: true, type, level };
    this.scrollToCenterLevel(level);
  }
  
  /**
   * Scroll to center the specified level in the viewport (like Python UI)
   */
  scrollToCenterLevel(level: number): void {
    if (!this.levelsContainer?.nativeElement) {
      console.log('[SCROLL] No levels container found');
      return;
    }
    
    const container = this.levelsContainer.nativeElement;
    // Use data-level attribute for reliable selection
    const levelWrapper = container.querySelector(`[data-level="${level}"]`) as HTMLElement;
    if (!levelWrapper) {
      console.log(`[SCROLL] Level wrapper not found for level ${level}`);
      return;
    }
    
    // Calculate position relative to container
    const levelLeft = levelWrapper.offsetLeft;
    const levelWidth = levelWrapper.offsetWidth;
    const containerWidth = container.clientWidth;
    const currentScroll = container.scrollLeft;
    
    // Check if level is already fully visible
    const levelRight = levelLeft + levelWidth;
    const visibleLeft = currentScroll;
    const visibleRight = currentScroll + containerWidth;
    
    // If pinned Level-1, account for its width
    let pinnedOffset = 0;
    if (this.pinLevel1 && level > 1) {
      const level1Wrapper = container.querySelector('[data-level="1"]') as HTMLElement;
      if (level1Wrapper) {
        pinnedOffset = level1Wrapper.offsetWidth;
      }
    }
    
    // Target: center the level in the viewport (accounting for pinned level)
    const effectiveContainerWidth = containerWidth - pinnedOffset;
    const targetScroll = levelLeft - pinnedOffset + (levelWidth / 2) - (effectiveContainerWidth / 2);
    
    // Clamp to valid scroll range
    const maxScroll = container.scrollWidth - containerWidth;
    const finalScroll = Math.max(0, Math.min(targetScroll, maxScroll));
    
    console.log(`[SCROLL] Scrolling to level ${level}: targetScroll=${finalScroll.toFixed(0)}, levelLeft=${levelLeft}, levelWidth=${levelWidth}`);
    
    // Animate the scroll (like Python UI)
    this.animateScroll(container, finalScroll, 300);
  }
  
  /**
   * Animate scroll with easing (OutCubic like Python UI)
   */
  private animateScroll(element: HTMLElement, targetScroll: number, duration: number): void {
    // Cancel any existing animation
    if (this.scrollAnimationId !== null) {
      cancelAnimationFrame(this.scrollAnimationId);
    }
    
    const startScroll = element.scrollLeft;
    const distance = targetScroll - startScroll;
    const startTime = performance.now();
    
    const easeOutCubic = (t: number): number => {
      return 1 - Math.pow(1 - t, 3);
    };
    
    const step = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      
      element.scrollLeft = startScroll + (distance * eased);
      
      if (progress < 1) {
        this.scrollAnimationId = requestAnimationFrame(step);
      } else {
        this.scrollAnimationId = null;
      }
    };
    
    this.scrollAnimationId = requestAnimationFrame(step);
  }
  
  onPaneFocused(level: number, type: 'nav' | 'work'): void {
    // Update pane order focus state
    this.paneOrder.forEach((pane, index) => {
      if (pane.level === level && pane.type === type) {
        pane.focused = true;
        this.currentPaneIndex = index;
      } else {
        pane.focused = false;
      }
    });
    this.focusedPane = { focused: true, type, level };
    // Scroll to center the focused level (like Python UI)
    this.scrollToCenterLevel(level);
  }
  
  // ==================== Level Resizing (like Python ResizeHandle) ====================
  
  startLevelResize(event: MouseEvent, levelIndex: number): void {
    event.preventDefault();
    this.isResizingLevel = true;
    this.resizingLevelIndex = levelIndex;
    this.startX = event.clientX;
    this.startWidth = this.levelWidths[levelIndex];
    
    document.addEventListener('mousemove', this.onLevelResize);
    document.addEventListener('mouseup', this.stopLevelResize);
    this.addDebugLog(`[RESIZE] Started resizing Level ${levelIndex + 1}`);
  }
  
  private onLevelResize = (event: MouseEvent): void => {
    if (!this.isResizingLevel) return;
    
    const deltaX = event.clientX - this.startX;
    const newWidth = this.startWidth + deltaX;
    
    // Enforce min/max constraints
    const clampedWidth = Math.max(
      ImageProcessingToolComponent.MIN_LEVEL_WIDTH,
      Math.min(ImageProcessingToolComponent.MAX_LEVEL_WIDTH, newWidth)
    );
    
    // Update the level being resized
    this.levelWidths[this.resizingLevelIndex] = clampedWidth;
    
    this.cdr.detectChanges();
  };
  
  private stopLevelResize = (): void => {
    if (this.isResizingLevel) {
      const finalWidth = this.levelWidths[this.resizingLevelIndex];
      this.addDebugLog(`[RESIZE] Level ${this.resizingLevelIndex + 1} resized to ${Math.round(finalWidth)}px`);
    }
    this.isResizingLevel = false;
    this.resizingLevelIndex = -1;
    document.removeEventListener('mousemove', this.onLevelResize);
    document.removeEventListener('mouseup', this.stopLevelResize);
  };
  
  onNavPaneResize(level: number, width: number): void {
    this.navPaneWidths[level - 1] = width;
    this.addDebugLog(`Level ${level} nav pane width: ${width.toFixed(1)}%`);
  }
  
  // ==================== Level Operations ====================
  
  getLevelState(level: number): LevelState | undefined {
    // Level is 1-indexed but array is 0-indexed
    return this.sessionState?.levels[level - 1];
  }

  /**
   * Get total image count across all levels
   */
  getTotalImageCount(): number {
    if (!this.sessionState?.levels) return 0;
    return this.sessionState.levels.reduce((total, level) => total + (level.images?.length || 0), 0);
  }
  
  async loadFolder(level: number): Promise<void> {
    // This will be triggered by file input in level-pane component
    this.addDebugLog(`Load folder requested for Level ${level}`);
  }
  
  async processFiles(level: number, files: FileList): Promise<void> {
    const levelIndex = level - 1;
    this.addDebugLog(`[BROWSE] Processing ${files.length} files for Level ${level}`);
    
    // Check for PDF files
    const pdfFiles = Array.from(files).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    const imageFilesArray = Array.from(files).filter(f => 
      /\.(png|jpg|jpeg|gif|bmp|webp)$/i.test(f.name)
    );
    
    // Clear downstream levels when loading new images (like Python UI)
    if (level === 1) {
      this.imageToolService.clearLevelsFrom(0);
      this.addDebugLog(`[BROWSE] Cleared all levels for fresh load`);
    }
    
    // Handle PDF files - these still need API for conversion
    if (pdfFiles.length > 0) {
      this.addDebugLog(`[BROWSE] Converting ${pdfFiles.length} PDF file(s) to images (requires API)...`);
      for (const pdf of pdfFiles) {
        try {
          await this.imageToolService.convertPdfToImages(pdf, level).toPromise();
          this.addDebugLog(`[BROWSE] PDF converted: ${pdf.name}`);
        } catch (error: any) {
          this.addDebugLog(`[BROWSE] ERROR converting PDF ${pdf.name}: ${error.message}`);
        }
      }
    }
    
    // Handle image files - load locally WITHOUT API call (like Python UI)
    if (imageFilesArray.length > 0) {
      this.addDebugLog(`[BROWSE] Loading ${imageFilesArray.length} image(s) locally (no API call)...`);
      
      try {
        // Load images locally using FileReader (no network request)
        const loadedImages = await this.imageToolService.loadImagesLocally(imageFilesArray, levelIndex).toPromise();
        
        if (loadedImages && loadedImages.length > 0) {
          this.addDebugLog(`[BROWSE] Successfully loaded ${loadedImages.length} image(s) locally`);
          
          // Log each loaded image
          loadedImages.forEach((img, idx) => {
            this.addDebugLog(`  [${idx + 1}] ${img.filename} (${img.width || '?'}x${img.height || '?'})`);
          });
          
          // Try to load saved hierarchy from database for the first image
          if (loadedImages.length > 0) {
            const firstImage = loadedImages[0];
            this.addDebugLog(`[HIERARCHY] Checking database for saved hierarchy of: ${firstImage.filename}`);
            
            try {
              await this.loadHierarchyForImage(firstImage.path || firstImage.filename);
            } catch (error: any) {
              // Hierarchy loading is optional - don't block on error
              this.addDebugLog(`[HIERARCHY] No saved hierarchy found or error: ${error.message || 'Unknown'}`);
            }
          }
        }
      } catch (error: any) {
        this.addDebugLog(`[BROWSE] ERROR loading images locally: ${error.message}`);
        // Fallback to API-based loading if local fails
        this.addDebugLog(`[BROWSE] Falling back to API-based loading...`);
        try {
          await this.imageToolService.uploadFiles(imageFilesArray).toPromise();
          this.addDebugLog(`[BROWSE] API upload successful`);
        } catch (apiError: any) {
          this.addDebugLog(`[BROWSE] API upload also failed: ${apiError.message}`);
        }
      }
    }
    
    this.snackBar.open(`Loaded ${files.length} file(s) to Level ${level}`, 'Close', { duration: 2000 });
    this.addDebugLog(`[BROWSE] Completed processing ${files.length} file(s)`);
  }
  
  /**
   * Load saved hierarchy from database for a source image
   */
  async loadHierarchyForImage(imagePath: string): Promise<void> {
    this.addDebugLog(`[HIERARCHY] Loading saved cuts/hierarchy from database...`);
    
    try {
      const response = await this.imageToolService.loadHierarchyFromDatabase(imagePath).toPromise();
      
      if (response?.success && response.hierarchy) {
        const levelCount = response.hierarchy.length || 0;
        const totalImages = response.hierarchy.reduce((sum: number, lvl: any) => sum + (lvl.images?.length || 0), 0);
        const existingFiles = response.hierarchy.reduce((sum: number, lvl: any) => sum + (lvl.existing_files || 0), 0);
        
        this.addDebugLog(`[HIERARCHY] Found saved hierarchy with ${levelCount} level(s), ${totalImages} image(s), ${existingFiles} exist on disk`);
        
        // Log what was loaded at each level
        if (Array.isArray(response.hierarchy)) {
          response.hierarchy.forEach((levelData: any) => {
            const lvlIdx = levelData.levelIndex ?? levelData.level_index ?? 0;
            const uiLevel = lvlIdx + 1;  // Convert 0-based to 1-based for display
            const imgCount = levelData.images?.length || 0;
            // Use explicit values from API, don't default
            const existing = levelData.existing_files !== undefined ? levelData.existing_files : 0;
            const missing = levelData.missing_files !== undefined ? levelData.missing_files : imgCount;
            const lineCount = levelData.lines?.length || 0;
            this.addDebugLog(`  Level ${uiLevel} (index ${lvlIdx}): ${imgCount} image(s) (${existing} exist, ${missing} missing), ${lineCount} marker(s)`);
          });
        }
        
        if (existingFiles > 0) {
          this.addDebugLog(`[HIERARCHY] Applying ${existingFiles} existing files to levels...`);
          this.snackBar.open(`Loaded saved hierarchy: ${existingFiles} files available`, 'Close', { duration: 3000 });
        } else {
          this.addDebugLog(`[HIERARCHY] All saved files are missing from disk - cannot restore`);
          this.snackBar.open('Saved cuts found but files no longer exist on disk', 'Close', { duration: 3000 });
        }
      } else {
        this.addDebugLog(`[HIERARCHY] No saved hierarchy found for this image`);
      }
    } catch (error: any) {
      // Don't show error to user - this is expected for new images
      this.addDebugLog(`[HIERARCHY] Database check complete - no hierarchy (${error.message || 'not found'})`);
    }
  }
  
  openImageInWindowsViewer(level: number, imageId: string): void {
    const filename = imageId.split(/[\\/]/).pop() || imageId;
    this.addDebugLog(`[EXTERNAL] Level ${level}: Opening "${filename}" in Windows default viewer...`);
    
    // Use the service's API to open the image externally
    this.imageToolService.openImageExternal(level - 1, imageId).subscribe({
      next: () => this.addDebugLog(`[EXTERNAL] SUCCESS - Image opened in external viewer`),
      error: (err) => {
        this.addDebugLog(`[EXTERNAL] ERROR - ${err.message}`);
        this.snackBar.open('Failed to open image', 'Close', { duration: 2000 });
      }
    });
  }
  
  async cutImage(level: number, imageId: string, cutLines: number[], cutType: 'vertical' | 'horizontal' = 'horizontal'): Promise<void> {
    const filename = imageId.split(/[\\\\]/).pop() || imageId;
    this.addDebugLog(`[CUT] Level ${level}: Starting ${cutType} cut of "${filename}"`);
    this.addDebugLog(`[CUT]   Cut positions: [${cutLines.join(', ')}] (${cutLines.length} line(s))`);
    
    try {
      const levelIndex = level - 1;
      this.addDebugLog(`[CUT]   Sending cut request to API...`);
      
      const result = await this.imageToolService.cutImageAtLines(levelIndex, imageId, cutLines, cutType).toPromise();
      
      if (result?.success) {
        const outputCount = result.output_files?.length || 0;
        this.addDebugLog(`[CUT] Level ${level}: SUCCESS - Created ${outputCount} piece(s)`);
        
        // Log each output file
        if (result.output_files) {
          result.output_files.forEach((file: any, idx: number) => {
            const name = file.name || file.path?.split(/[\\/]/).pop() || 'unknown';
            this.addDebugLog(`[CUT]   [${idx + 1}] ${name}`);
          });
        }
        
        this.addDebugLog(`[CUT]   Output sent to Level ${level + 1}`);
        this.snackBar.open(`Image cut into ${outputCount} pieces`, 'Close', { duration: 1500 });
        
        // Focus on next level after cut
        this.focusPaneWithScroll(level + 1);
      } else {
        this.addDebugLog(`[CUT] Level ${level}: FAILED - ${result?.message || 'Unknown error'}`);
        this.snackBar.open('Failed to cut image', 'Close', { duration: 2000 });
      }
    } catch (error: any) {
      this.addDebugLog(`[CUT] Level ${level}: ERROR - ${error.message}`);
      this.addDebugLog(`[CUT]   Stack: ${error.stack?.split('\n')[1] || 'N/A'}`);
      this.snackBar.open('Failed to cut image', 'Close', { duration: 2000 });
    }
  }
  
  async cropImage(level: number, imageId: string, cropRect: { x: number; y: number; width: number; height: number }): Promise<void> {
    const filename = imageId.split(/[\\/]/).pop() || imageId;
    this.addDebugLog(`[CROP] Level ${level}: Starting crop of "${filename}"`);
    this.addDebugLog(`[CROP]   Region: x=${Math.round(cropRect.x)}, y=${Math.round(cropRect.y)}, w=${Math.round(cropRect.width)}, h=${Math.round(cropRect.height)}`);
    
    try {
      const levelIndex = level - 1;
      this.addDebugLog(`[CROP]   Sending crop request to API...`);
      
      const result = await this.imageToolService.cropImageWithRect(levelIndex, imageId, cropRect).toPromise();
      
      if (result?.success) {
        this.addDebugLog(`[CROP] Level ${level}: SUCCESS - Image cropped and replaced`);
        this.snackBar.open('Image cropped successfully', 'Close', { duration: 1500 });
      } else {
        this.addDebugLog(`[CROP] Level ${level}: FAILED - ${result?.message || 'Unknown error'}`);
        this.snackBar.open('Failed to crop image', 'Close', { duration: 2000 });
      }
    } catch (error: any) {
      this.addDebugLog(`[CROP] Level ${level}: ERROR - ${error.message}`);
      this.snackBar.open('Failed to crop image', 'Close', { duration: 2000 });
    }
  }
  
  async joinImages(level: number, direction: 'vertical' | 'horizontal'): Promise<void> {
    const levelState = this.getLevelState(level);
    if (!levelState || levelState.selectedImages.length < 2) {
      this.addDebugLog(`[JOIN] Level ${level}: Need at least 2 selected images to join`);
      this.snackBar.open('Select at least 2 images to join', 'Close', { duration: 2000 });
      return;
    }
    
    const count = levelState.selectedImages.length;
    this.addDebugLog(`[JOIN] Level ${level}: Starting ${direction} join of ${count} image(s)`);
    
    // Log selected images
    levelState.selectedImages.forEach((imgId, idx) => {
      const img = levelState.images.find(i => i.id === imgId);
      this.addDebugLog(`[JOIN]   [${idx + 1}] ${img?.filename || imgId}`);
    });
    
    try {
      const levelIndex = level - 1;
      this.addDebugLog(`[JOIN]   Sending join request to API...`);
      
      const result = await this.imageToolService.joinSelectedImages(levelIndex, levelState.selectedImages, direction).toPromise();
      
      if (result?.success) {
        this.addDebugLog(`[JOIN] Level ${level}: SUCCESS - ${count} images joined ${direction}ly`);
        this.snackBar.open('Images joined successfully', 'Close', { duration: 1500 });
      } else {
        this.addDebugLog(`[JOIN] Level ${level}: FAILED - ${result?.message || 'Unknown error'}`);
        this.snackBar.open('Failed to join images', 'Close', { duration: 2000 });
      }
    } catch (error: any) {
      this.addDebugLog(`[JOIN] Level ${level}: ERROR - ${error.message}`);
      this.snackBar.open('Failed to join images', 'Close', { duration: 2000 });
    }
  }
  
  async undoJoin(level: number): Promise<void> {
    this.addDebugLog(`[UNDO] Level ${level}: Undoing last join operation...`);
    
    try {
      const levelIndex = level - 1;
      await this.imageToolService.undoJoin(levelIndex).toPromise();
      this.addDebugLog(`[UNDO] Level ${level}: SUCCESS - Join undone`);
      this.snackBar.open('Join undone', 'Close', { duration: 1500 });
    } catch (error: any) {
      this.addDebugLog(`[UNDO] Level ${level}: ERROR - ${error.message}`);
      this.snackBar.open('Failed to undo join', 'Close', { duration: 2000 });
    }
  }
  
  async moveToNextLevel(level: number): Promise<void> {
    const levelState = this.getLevelState(level);
    if (!levelState || levelState.selectedImages.length === 0) {
      this.addDebugLog(`[MOVE] Level ${level}: No images selected to move`);
      this.snackBar.open('Select images to move', 'Close', { duration: 2000 });
      return;
    }
    
    const nextLevel = level + 1;
    if (nextLevel > 5) {
      this.addDebugLog(`[MOVE] Level ${level}: Cannot move beyond Level 5`);
      this.snackBar.open('Already at last level', 'Close', { duration: 2000 });
      return;
    }
    
    const count = levelState.selectedImages.length;
    this.addDebugLog(`[MOVE] Moving ${count} image(s) from Level ${level} to Level ${nextLevel}`);
    
    try {
      const fromLevelIndex = level - 1;
      const toLevelIndex = nextLevel - 1;
      await this.imageToolService.moveToLevel(fromLevelIndex, toLevelIndex, levelState.selectedImages).toPromise();
      this.addDebugLog(`[MOVE] SUCCESS - ${count} image(s) moved to Level ${nextLevel}`);
      this.snackBar.open(`Moved to Level ${nextLevel}`, 'Close', { duration: 1500 });
      
      // Focus on next level after move
      this.focusPaneWithScroll(nextLevel);
    } catch (error: any) {
      this.addDebugLog(`[MOVE] ERROR - ${error.message}`);
      this.snackBar.open('Failed to move images', 'Close', { duration: 2000 });
    }
  }
  
  async deleteSelectedImages(level: number): Promise<void> {
    const levelState = this.getLevelState(level);
    if (!levelState || levelState.selectedImages.length === 0) {
      this.addDebugLog(`[DELETE] Level ${level}: No images selected to delete`);
      return;
    }
    
    const count = levelState.selectedImages.length;
    this.addDebugLog(`[DELETE] Level ${level}: Deleting ${count} selected image(s)`);
    
    // Log images being deleted
    levelState.selectedImages.forEach((imgId, idx) => {
      const img = levelState.images.find(i => i.id === imgId);
      this.addDebugLog(`[DELETE]   [${idx + 1}] ${img?.filename || imgId}`);
    });
    
    try {
      const levelIndex = level - 1;
      const result = await this.imageToolService.deleteImages(levelIndex, levelState.selectedImages).toPromise();
      
      if (result?.success) {
        this.addDebugLog(`[DELETE] Level ${level}: SUCCESS - ${count} image(s) deleted`);
        this.snackBar.open(`${count} image(s) deleted`, 'Close', { duration: 1500 });
      } else {
        this.addDebugLog(`[DELETE] Level ${level}: FAILED - ${result?.message || 'Unknown error'}`);
        this.snackBar.open('Failed to delete images', 'Close', { duration: 2000 });
      }
    } catch (error: any) {
      this.addDebugLog(`[DELETE] Level ${level}: ERROR - ${error.message}`);
      this.snackBar.open('Failed to delete images', 'Close', { duration: 2000 });
    }
  }
  
  // Delete a single image directly (from thumbnail delete button)
  async deleteSingleImage(level: number, imageId: string): Promise<void> {
    if (!imageId) {
      this.addDebugLog(`[DELETE] Level ${level}: No imageId provided`);
      return;
    }
    
    const filename = imageId.split(/[\\/]/).pop() || imageId;
    this.addDebugLog(`[DELETE] Level ${level}: Deleting single image "${filename}"`);
    
    try {
      const levelIndex = level - 1;
      this.addDebugLog(`[DELETE]   Sending delete request to API...`);
      
      const result = await this.imageToolService.deleteImages(levelIndex, [imageId]).toPromise();
      
      if (result?.success) {
        this.addDebugLog(`[DELETE] Level ${level}: SUCCESS - Image deleted`);
        this.snackBar.open('Image deleted', 'Close', { duration: 1500 });
      } else {
        this.addDebugLog(`[DELETE] Level ${level}: FAILED - ${result?.message || 'Unknown error'}`);
        this.snackBar.open('Failed to delete image', 'Close', { duration: 2000 });
      }
    } catch (error: any) {
      this.addDebugLog(`[DELETE] Level ${level}: ERROR - ${error.message}`);
      this.snackBar.open('Failed to delete image', 'Close', { duration: 2000 });
    }
  }

  /**
   * Hierarchical delete: Delete all cuts at specified level and all subsequent levels.
   * This matches Python's _hierarchical_delete_all() with full database integration.
   */
  async hierarchicalDeleteLevel(level: number): Promise<void> {
    const levelState = this.getLevelState(level);
    if (!levelState) {
      this.addDebugLog(`[HIERARCHICAL_DELETE] Level ${level}: Invalid level`);
      return;
    }

    const imageCount = levelState.images.length;
    const childLevels = 5 - level; // How many levels will be affected

    // Confirm deletion
    const confirmMsg = imageCount > 0
      ? `Delete all ${imageCount} image(s) from Level ${level} and clear ${childLevels} child level(s)?\n\nThis will:\n- Delete images from database\n- Delete physical files\n- Clear all subsequent levels (${level + 1} to 5)\n\nThis cannot be undone.`
      : `Clear Level ${level} and all ${childLevels} child level(s)?`;

    const confirmed = confirm(confirmMsg);
    if (!confirmed) {
      this.addDebugLog(`[HIERARCHICAL_DELETE] Level ${level}: User cancelled`);
      return;
    }

    this.addDebugLog(`[HIERARCHICAL_DELETE] Level ${level}: Starting hierarchical delete...`);
    this.addDebugLog(`[HIERARCHICAL_DELETE]   Images in level: ${imageCount}`);
    this.addDebugLog(`[HIERARCHICAL_DELETE]   Child levels to clear: ${childLevels}`);

    try {
      const levelIndex = level - 1;
      const result = await this.imageToolService.hierarchicalDeleteLevel(levelIndex).toPromise();

      if (result?.success) {
        this.addDebugLog(`[HIERARCHICAL_DELETE] Level ${level}: ✓ SUCCESS`);
        this.addDebugLog(`[HIERARCHICAL_DELETE]   - Levels cleared: ${result.levels_cleared}`);
        this.addDebugLog(`[HIERARCHICAL_DELETE]   - DB cuts deleted: ${result.db_cuts_deleted}`);
        this.addDebugLog(`[HIERARCHICAL_DELETE]   - Files deleted: ${result.files_deleted}`);
        
        const message = result.db_cuts_deleted > 0
          ? `Deleted ${result.levels_cleared} level(s), ${result.db_cuts_deleted} cut(s) from DB, ${result.files_deleted} file(s)`
          : `Cleared ${result.levels_cleared} level(s), deleted ${result.files_deleted} file(s)`;
        
        this.snackBar.open(message, 'Close', { duration: 3000 });
      } else {
        this.addDebugLog(`[HIERARCHICAL_DELETE] Level ${level}: ✗ FAILED - ${result?.message || 'Unknown error'}`);
        this.snackBar.open('Hierarchical delete failed', 'Close', { duration: 2000 });
      }
    } catch (error: any) {
      this.addDebugLog(`[HIERARCHICAL_DELETE] Level ${level}: ✗ ERROR - ${error.message}`);
      this.snackBar.open('Hierarchical delete error', 'Close', { duration: 2000 });
    }
  }
  
  onSelectAll(level: number, selectAll: boolean): void {
    const levelIndex = level - 1;
    if (selectAll) {
      this.imageToolService.selectAllImages(levelIndex);
      this.addDebugLog(`[SELECT] Level ${level}: Selected all images`);
    } else {
      this.imageToolService.clearSelections(levelIndex);
      this.addDebugLog(`[SELECT] Level ${level}: Deselected all images`);
    }
  }
  
  async undo(): Promise<void> {
    const level = this.focusedPane.level;
    this.addDebugLog(`[UNDO] Level ${level}: Performing undo...`);
    
    try {
      const levelIndex = level - 1;
      await this.imageToolService.undo(levelIndex).toPromise();
      this.addDebugLog(`[UNDO] Level ${level}: SUCCESS`);
    } catch (error: any) {
      this.addDebugLog(`[UNDO] Level ${level}: ERROR - ${error.message}`);
    }
  }
  
  async redo(): Promise<void> {
    const level = this.focusedPane.level;
    this.addDebugLog(`[REDO] Level ${level}: Performing redo...`);
    
    try {
      const levelIndex = level - 1;
      await this.imageToolService.redo(levelIndex).toPromise();
      this.addDebugLog(`[REDO] Level ${level}: SUCCESS`);
    } catch (error: any) {
      this.addDebugLog(`[REDO] Level ${level}: ERROR - ${error.message}`);
    }
  }
  
  async saveLevel(level: number): Promise<void> {
    const levelState = this.getLevelState(level);
    if (!levelState || levelState.images.length === 0) {
      this.addDebugLog(`[SAVE] Level ${level}: No images to save`);
      this.snackBar.open('No images to save', 'Close', { duration: 2000 });
      return;
    }
    
    const imageCount = levelState.images.length;
    this.addDebugLog(`[SAVE] Level ${level}: Starting save of ${imageCount} image(s) to database...`);
    
    // Log images being saved
    levelState.images.forEach((img, idx) => {
      this.addDebugLog(`[SAVE]   [${idx + 1}] ${img.filename || img.name || 'unknown'}`);
    });
    
    try {
      const levelIndex = level - 1;
      this.addDebugLog(`[SAVE]   Sending save request to API...`);
      
      const result = await this.imageToolService.saveToDatabase(levelIndex).toPromise();
      
      if (result?.success) {
        this.addDebugLog(`[SAVE] Level ${level}: SUCCESS - ${imageCount} image(s) saved to database`);
        if (result.saved_count !== undefined) {
          this.addDebugLog(`[SAVE]   Database records created: ${result.saved_count}`);
        }
        this.snackBar.open(`${imageCount} image(s) saved to database`, 'Close', { duration: 1500 });
      } else {
        this.addDebugLog(`[SAVE] Level ${level}: FAILED - ${result?.message || 'Unknown error'}`);
        this.snackBar.open('Failed to save', 'Close', { duration: 2000 });
      }
    } catch (error: any) {
      this.addDebugLog(`[SAVE] Level ${level}: ERROR - ${error.message}`);
      this.addDebugLog(`[SAVE]   This may mean the API is not connected`);
      this.snackBar.open('Failed to save', 'Close', { duration: 2000 });
    }
  }
  
  async uploadToGcs(level: number): Promise<void> {
    const levelState = this.getLevelState(level);
    if (!levelState || levelState.images.length === 0) {
      this.snackBar.open('No images to upload', 'Close', { duration: 2000 });
      return;
    }
    
    this.addDebugLog(`Uploading Level ${level} to GCS...`);
    
    try {
      const levelIndex = level - 1;
      await this.imageToolService.uploadToGcs(levelIndex).toPromise();
      this.snackBar.open('Uploaded to GCS', 'Close', { duration: 1500 });
    } catch (error: any) {
      this.addDebugLog(`Error uploading: ${error.message}`);
      this.snackBar.open('Failed to upload', 'Close', { duration: 2000 });
    }
  }
  
  // ==================== Debug Logging ====================
  
  addDebugLog(message: string): void {
    const timestamp = new Date().toLocaleTimeString();
    this.debugLogs.push(`[${timestamp}] ${message}`);
    
    // Keep only last 100 logs
    if (this.debugLogs.length > 100) {
      this.debugLogs.shift();
    }
  }
  
  toggleDebugPanel(): void {
    this.showDebugPanel = !this.showDebugPanel;
  }
  
  toggleLogSection(): void {
    this.showLogSection = !this.showLogSection;
  }
  
  clearDebugLogs(): void {
    this.debugLogs = [];
  }

  copyLogsToClipboard(): void {
    const logText = this.debugLogs.join('\n');
    navigator.clipboard.writeText(logText).then(() => {
      this.snackBar.open('Logs copied to clipboard', 'Close', { duration: 2000 });
    }).catch(err => {
      this.addDebugLog('Failed to copy logs: ' + err);
    });
  }
  
  // ==================== File Upload (like Python UI) ====================
  
  async browseFiles(): Promise<void> {
    this.addDebugLog(`[UI] Browse Files button clicked`);
    
    // Ensure session exists before file upload
    if (!this.sessionState?.sessionId) {
      this.addDebugLog('[SESSION] No session - initializing local session...');
      this.initializeLocalSession();
    }
    
    // Create a file input and trigger it
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.png,.jpg,.jpeg,.gif,.bmp,.webp,.pdf';
    input.onchange = (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        this.addDebugLog(`[UI] User selected ${files.length} file(s) from file browser`);
        
        // Log each selected file
        Array.from(files).forEach((f, idx) => {
          const sizeKB = (f.size / 1024).toFixed(1);
          this.addDebugLog(`  [${idx + 1}] ${f.name} (${sizeKB} KB)`);
        });
        
        // Process files - will load locally without API call
        this.processFiles(1, files); // Load to Level 1
      } else {
        this.addDebugLog(`[UI] File browser cancelled or no files selected`);
      }
    };
    input.click();
  }
  
  async browseFolder(): Promise<void> {
    this.addDebugLog(`[UI] Browse Folder button clicked`);
    
    // Ensure session exists before file upload
    if (!this.sessionState?.sessionId) {
      this.addDebugLog('[SESSION] No session - initializing local session...');
      this.initializeLocalSession();
    }
    
    // Create a folder input
    const input = document.createElement('input');
    input.type = 'file';
    input.webkitdirectory = true;
    (input as any).directory = true;
    input.onchange = (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        this.addDebugLog(`[UI] Folder contains ${files.length} total file(s)`);
        
        // Filter image files
        const imageFiles = Array.from(files).filter(f => 
          /\.(png|jpg|jpeg|gif|bmp|webp|pdf)$/i.test(f.name)
        );
        
        this.addDebugLog(`[UI] Found ${imageFiles.length} image/PDF file(s) in folder`);
        
        if (imageFiles.length > 0) {
          // Log each image file
          imageFiles.slice(0, 10).forEach((f, idx) => {
            const sizeKB = (f.size / 1024).toFixed(1);
            this.addDebugLog(`  [${idx + 1}] ${f.name} (${sizeKB} KB)`);
          });
          if (imageFiles.length > 10) {
            this.addDebugLog(`  ... and ${imageFiles.length - 10} more file(s)`);
          }
          
          const dataTransfer = new DataTransfer();
          imageFiles.forEach(f => dataTransfer.items.add(f));
          this.processFiles(1, dataTransfer.files);
        } else {
          this.addDebugLog(`[UI] No supported image files found in folder`);
          this.snackBar.open('No image files found in folder', 'Close', { duration: 2000 });
        }
      } else {
        this.addDebugLog(`[UI] Folder browser cancelled or empty folder`);
      }
    };
    input.click();
  }
  
  /**
   * Add a new level (Level-6 etc.)
   */
  addLevel(): void {
    const newLevel = this.imageToolService.addLevel();
    if (newLevel) {
      const levelNum = newLevel.index + 1;
      this.addDebugLog(`[LEVEL] Added Level-${levelNum}`);
      this.snackBar.open(`Level-${levelNum} added`, 'Close', { duration: 1500 });
      
      // Initialize pane width for new level
      this.levelPaneWidths.push(25);
    } else {
      this.snackBar.open('Failed to add level', 'Close', { duration: 2000 });
    }
  }
  
  resetAll(): void {
    // Reset all levels
    this.addDebugLog('[RESET] Resetting all levels...');
    this.imageToolService.clearLevelsFrom(0);
    this.initializeLocalSession();
    this.addDebugLog('[RESET] All levels cleared, fresh session started');
    this.snackBar.open('Reset complete', 'Close', { duration: 2000 });
  }
  
  /**
   * Create a new session (can be API-based or local)
   */
  async createSession(): Promise<void> {
    this.addDebugLog('[SESSION] Creating new session...');
    this.isLoading = true;
    
    try {
      // Try API-based session first
      await this.imageToolService.createSession().toPromise();
      this.addDebugLog('[SESSION] API session created successfully');
    } catch (error: any) {
      // Fallback to local session
      this.addDebugLog(`[SESSION] API session failed (${error.message}), using local session`);
      this.initializeLocalSession();
    } finally {
      this.isLoading = false;
    }
  }
  
  // ==================== UI Helpers ====================
  
  trackByLevel(index: number): number {
    return index + 1;
  }
}
